import dayjs from "dayjs";
import ky from "ky";
import { put } from "redux-saga/effects";

import * as Sentry from "@sentry/react";

import { getDate } from "@ip-utils";

import { appActions } from "./actions";

export function* sendRequest(action, method, url, options, successMessage) {
  yield put(appActions.storeLoading(action.storageKey || action.type, true));

  options = Object.assign(
    {
      method: method || "get",
      throwHttpErrors: false,
    },
    options || {}
  );

  options.headers = Object.assign(options.headers || {}, {
    Accept: "application/json",
  });

  if (action.payload) {
    options.json = yield parseData(action.payload.data);
    options.searchParams = yield parseData(action.payload.params);

    if (options.json) {
      const files = Object.values(options.json).filter(
        (item) => item instanceof window.File || item instanceof window.FileList
      );

      // In case of files being sent, JSON format won't do and a FormData needs to be used
      // This converts the JSON to FormData and adds the files correctly
      if (files.length) {
        const formData = new FormData();
        Object.entries(options.json).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (item instanceof window.Object) {
                item = JSON.stringify(item);
              }
              formData.append(`${key}[]`, item);
            });
          } else if (value instanceof window.FileList) {
            Array.from(value).forEach((item) => {
              formData.append(`${key}[]`, item);
            });
          } else {
            formData.append(key, value);
          }
        });

        options.body = formData;
        options.timeout = 3 * 60 * 1000;
        delete options.json;
      }
    }
  }

  let response, responseData;

  try {
    response = yield ky(url, options);

    if (!response) {
      throw new Error();
    }

    responseData = yield response.json().catch(() => null);

    if (response.ok) {
      if (successMessage) {
        yield put(appActions.storeNotification("success", successMessage));
      }

      if (action.hasOwnProperty("onSuccess")) {
        action.onSuccess(responseData, action);
      }
    } else if (response.status !== 401) {
      yield put(
        appActions.storeNotification(
          "error",
          (responseData && responseData.message) || response.statusText
        )
      );

      if (action.hasOwnProperty("onError")) {
        action.onError(responseData, action);
      }
    }
  } catch (e) {
    yield put(
      appActions.storeNotification("error", e.message || "Server Error")
    );

    if (e.message) {
      e.message = `${e.message} | ${method} | ${url}`;
      Sentry.captureException(e, {
        tags: {
          action: action.type,
          method,
          url,
        },
      });
    }
  }

  yield put(appActions.storeLoading(action.storageKey || action.type, false));

  return Object.assign(response || {}, {
    data: responseData || {},
  });
}

function parseData(data) {
  if (data) {
    const parsed = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof Date || dayjs.isDayjs(value)) {
        value = getDate(value).toISOString(true);
      }
      parsed[key] = value;
    });
    return parsed;
  }
  return data;
}
